import React, { ErrorInfo } from 'react';
import hoistNonReactStatic from 'hoist-non-react-statics';
import { reportWhitePageError } from '@utils/owlReporter';
import Alert from '@roo/roo/Alert';
import defaultFallback, { IFallbackProps } from './defaultFallback';

interface IErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
    timestamp: number | null;
}

interface IErrorBoundaryProps {
    [propName: string]: any; // 允许额外的属性
    onError: () => void;
    forwardedRef: React.Ref<any>;
}

const withErrorBoundary = (
    Fallback?: React.FunctionComponent<IFallbackProps>,
    customerOptions?: { name: string },
) => (WrappedComponent: React.ComponentType<any>) => {
    class ErrorBoundary extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
        constructor(props: IErrorBoundaryProps) {
            super(props);
            this.state = {
                hasError: false,
                error: null,
                errorInfo: null,
                timestamp: null,
            };
        }

        static getDerivedStateFromError() {
            return {
                hasError: true,
            };
        }

        componentDidCatch(error: Error, errorInfo: ErrorInfo) {
            const timestamp = new Date().getTime();
            this.setState({
                error,
                errorInfo,
                timestamp,
            });
            const { onError } = this.props;
            if (onError && typeof onError === 'function') {
                onError();
            }
            // 采集页面白屏异常日志
            reportWhitePageError(error, errorInfo, customerOptions);
        }

        render() {
            const { hasError } = this.state;
            const FallbackComponent = Fallback || defaultFallback;
            if (hasError) {
                const { error, errorInfo, timestamp } = this.state;
                // 错误 UI
                return (
                    <FallbackComponent error={error} errorInfo={errorInfo} timestamp={timestamp} />
                );
            }
            const { forwardedRef, ...rest } = this.props;
            return <WrappedComponent ref={forwardedRef} {...rest} />; // eslint-disable-line react/jsx-props-no-spreading
        }
    }

    const forwarded = React.forwardRef<any, IErrorBoundaryProps>((props, forwardedRef) => (
        <ErrorBoundary {...props} ref={forwardedRef} /> // eslint-disable-line react/jsx-props-no-spreading
    ));
    return hoistNonReactStatic(forwarded, WrappedComponent);
};

export const withBlankFallbackEB = withErrorBoundary(() => null);
export const withDefaultFallbackEB = withErrorBoundary(); // 默认 Fallback UI
export const createCustomWithErrorBoundaryForOrderCard = (moduleName: string) => {
    const OrderCardFallbackUI: React.FC = () => {
        return (
            <Alert
                style={{ border: '1px solid rgba(247, 108, 108, 0.2)', borderRadius: 2 }}
                type="danger"
                title={`${moduleName}模块加载失败，请稍后重试`}
                showIcon={false}
                closable={false}
            />
        );
    };

    return withErrorBoundary(OrderCardFallbackUI, { name: moduleName });
};
export const createCustomWithErrorBoundaryForShellHead = (moduleName: string) => {
    const ShellHeadFallbackUI: React.FC = () => {
        return (
            <Alert
                style={{ border: '1px solid rgba(247, 108, 108, 0.2)', borderRadius: 2 }}
                type="danger"
                title={`${moduleName}模块加载失败，请刷新页面`}
                showIcon={false}
                closable={false}
                extra={
                    <div onClick={() => window.location.reload()} style={{ cursor: 'pointer' }}>
                        刷新
                    </div>
                }
            />
        );
    };

    return withErrorBoundary(ShellHeadFallbackUI, { name: moduleName });
};
export const createCustomWithErrorBoundaryForShellMenu = (moduleName: string) => {
    const ShellHeadFallbackUI: React.FC = () => {
        return (
            <div
                style={{
                    writingMode: 'vertical-rl',
                    width: '50px',
                    height: '100%',
                    lineHeight: '30px',
                    border: '1px solid rgba(247, 108, 108, 0.2)',
                    background: 'rgba(247, 108, 108, 0.1)',
                    padding: '15px 10px',
                }}
            >
                {moduleName}模块加载失败，请
                <span
                    onClick={() => window.location.reload()}
                    style={{ cursor: 'pointer', color: '#F76C6C' }}
                >
                    刷新
                </span>
                页面
            </div>
        );
    };

    return withErrorBoundary(ShellHeadFallbackUI, { name: moduleName });
};

export default withErrorBoundary;
